<template>
  <div class="publication-details">
    <v-card
      outlined
      class="mb-4"
    >
      <v-card-title>
        {{ $t('admin.productsDatabase.importWizard.steps.publicationDetails.title') }}
      </v-card-title>
      
      <v-card-text>
        <v-row>
          <!-- Publisher Column -->
          <v-col
            cols="12"
            md="6"
          >
            <v-card
              outlined
              class="h-100"
            >
              <v-card-title>
                {{ $t('admin.productsDatabase.importWizard.steps.publicationDetails.publisher') }}
              </v-card-title>
              <v-card-text>
                <PublisherSelector 
                  :product-info-manager="productInfoManager"
                  field-name="publisher"
                  :initial-publisher="getPublisher()"
                />
              </v-card-text>
            </v-card>
          </v-col>
          
          <!-- Subjects Column -->
          <v-col
            cols="12"
            md="6"
          >
            <v-card
              outlined
              class="h-100"
            >
              <v-card-title>
                {{ $t('admin.productsDatabase.importWizard.steps.publicationDetails.subjects') }}
              </v-card-title>
              <v-card-text>
                <SubjectSelector 
                  :book-id="bookId"
                  :initial-subjects="getSubjects()"
                  @subjects-updated="handleSubjectsUpdated"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        
        <!-- Published Date and Language Row -->
        <v-row class="mt-4">
          <v-col
            cols="12"
            md="6"
          >
            <v-card outlined>
              <v-card-title>
                {{ $t('admin.productsDatabase.importWizard.steps.publicationDetails.publishedDate') }}
              </v-card-title>
              <v-card-text>
                <LocalBookDatePicker
                  :value="getPublishedOn()"
                  :label="$t('admin.productsDatabase.importWizard.steps.publicationDetails.publishedDateLabel')"
                  @input="handlePublishedDateInput"
                />
              </v-card-text>
            </v-card>
          </v-col>
          
          <v-col
            cols="12"
            md="6"
          >
            <v-card outlined>
              <v-card-title>
                {{ $t('admin.productsDatabase.importWizard.steps.publicationDetails.language') }}
              </v-card-title>
              <v-card-text>
                <LocalBookLanguageSelect
                  :product-info-manager="productInfoManager"
                  field-name="language"
                  :label="$t('admin.productsDatabase.importWizard.steps.publicationDetails.languageLabel')"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import SubjectSelector from './SubjectSelector.vue';
import PublisherSelector from './PublisherSelector.vue';
import LocalBookDatePicker from '@/components/LocalBookDatePicker.vue';
import LocalBookLanguageSelect from '@/components/LocalBookLanguageSelect.vue';

export default {
  name: 'PublicationDetails',
  components: {
    SubjectSelector,
    PublisherSelector,
    LocalBookDatePicker,
    LocalBookLanguageSelect
  },
  props: {
    productInfoManager: {
      type: Object,
      required: true
    }
  },
  emits: ['valid'],
  computed: {
    bookId() {
      return this.productInfoManager?.getBookId() || '';
    },
    productInfo() {
      return this.productInfoManager?.getData();
    },
    isValid() {
      const book = this.productInfo?.book;
      if (!book) return false;
      
      // Publisher is required
      const hasPublisher = !!book.publisher;
      
      // Language is required
      const hasLanguage = !!book.language;

      const hasSubjects = book.subjects && book.subjects.edges && book.subjects.edges.length > 0;
      
      return hasPublisher && hasLanguage && hasSubjects;
    }
  },
  watch: {
    isValid(newVal) {
      this.$emit('valid', newVal);
    }
  },
  mounted() {
    // Emit initial validation state
    this.$emit('valid', this.isValid);
  },
  methods: {
    getSubjects() {
      if (this.productInfo && this.productInfo.book && this.productInfo.book.subjects) {
        if (this.productInfo.book.subjects.edges) {
          return this.productInfo.book.subjects.edges;
        } else {
          return this.productInfo.book.subjects;
        }
      }
      return [];
    },
    
    getPublisher() {
      if (this.productInfo && this.productInfo.book && this.productInfo.book.publisher) {
        return this.productInfo.book.publisher;
      }
      return null;
    },
    
    getPublishedOn() {
      if (this.productInfo && this.productInfo.book && this.productInfo.book.publishedOn) {
        return this.productInfo.book.publishedOn;
      }
      return null;
    },
    
    getLanguage() {
      if (this.productInfo && this.productInfo.book && this.productInfo.book.language) {
        return this.productInfo.book.language;
      }
      return null;
    },
    
    handleSubjectsUpdated(subjects) {
      // Create a structure similar to what the API returns
      const subjectsConnection = {
        edges: subjects.map(subject => ({
          node: subject
        }))
      };
      
      if (this.productInfoManager) {
        // SubjectSelector already saved via its own mutations, just update local state
        this.productInfoManager.updateFieldAlreadySaved('book.subjects', subjectsConnection);
      }
    },
    
    
    
    handlePublishedDateInput(newDate) {
      if (this.productInfoManager) {
        // Published date should save immediately via updateLocalBook
        this.productInfoManager.updateField('book.publishedOn', newDate);
      }
    },
    
    handleFieldUpdateError(error) {
      console.error('Error updating field:', error);
    }
  }
};
</script>

<style scoped>
.publication-details {
  padding: 16px;
}

.h-100 {
  height: 100%;
}
</style>
